import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { apiClient } from '../lib/api-client';

type Currency = {
  id: string;
  code: string;
  name: string;
  symbol: string;
  useDecimals: boolean;
  decimalPlaces: number;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

function EditCurrencyPage() {
  const navigate = useNavigate();
  const { currencyId } = useParams<{ currencyId: string }>();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currency, setCurrency] = useState<Currency | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    symbol: '',
    useDecimals: true,
    decimalPlaces: 2,
    displayOrder: 999,
    isActive: true
  });

  useEffect(() => {
    if (currencyId) {
      loadCurrency();
    }
  }, [currencyId]);

  const loadCurrency = async () => {
    if (!currencyId) return;

    try {
      setLoadingData(true);
      const data = await apiClient.getCurrency(currencyId);
      setCurrency(data.currency);

      // Initialize form data
      setFormData({
        name: data.currency.name,
        symbol: data.currency.symbol,
        useDecimals: data.currency.useDecimals,
        decimalPlaces: data.currency.decimalPlaces,
        displayOrder: data.currency.displayOrder,
        isActive: data.currency.isActive
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load currency');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currencyId) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate required fields
      if (!formData.name || !formData.symbol) {
        throw new Error('Name and symbol are required');
      }

      const result = await apiClient.updateCurrency(currencyId, {
        name: formData.name,
        symbol: formData.symbol,
        useDecimals: formData.useDecimals,
        decimalPlaces: formData.decimalPlaces,
        displayOrder: formData.displayOrder,
        isActive: formData.isActive
      });

      setSuccess(`Currency updated successfully: ${result.currency.code.toUpperCase()}`);

      setTimeout(() => {
        navigate('/settings');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update currency');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loadingData) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <div className='text-lg text-gray-600 dark:text-gray-400'>Loading currency...</div>
      </div>
    );
  }

  if (!currency) {
    return (
      <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/30'>
        <p className='text-red-800 dark:text-red-400'>Currency not found</p>
        <button
          onClick={() => navigate('/settings')}
          className='mt-2 rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800'
        >
          Back to Service Settings
        </button>
      </div>
    );
  }

  return (
    <div className='mx-auto max-w-2xl space-y-6'>
      <div className='flex items-center gap-4'>
        <button
          onClick={() => navigate('/settings')}
          className='text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
        >
          ← Back to Service Settings
        </button>
        <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>
          Edit Currency: {currency.code.toUpperCase()}
        </h1>
      </div>

      {error && (
        <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/30'>
          <p className='text-red-800 dark:text-red-400'>{error}</p>
        </div>
      )}

      {success && (
        <div className='rounded-md border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/30'>
          <p className='text-green-800 dark:text-green-400'>{success}</p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className='space-y-6 rounded-lg bg-white p-6 shadow-md dark:bg-gray-800 dark:shadow-gray-900/50'
      >
        {/* Currency Code (Read-only) */}
        <div>
          <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'>
            Currency Code
          </label>
          <input
            type='text'
            value={currency.code.toUpperCase()}
            disabled
            className='w-full cursor-not-allowed rounded-md border border-gray-300 bg-gray-100 px-3 py-2 font-mono text-gray-600 uppercase dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400'
          />
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Currency code cannot be changed
          </p>
        </div>

        {/* Currency Name */}
        <div>
          <label
            htmlFor='name'
            className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            Currency Name <span className='text-red-500'>*</span>
          </label>
          <input
            type='text'
            id='name'
            value={formData.name}
            onChange={e => handleChange('name', e.target.value)}
            required
            placeholder='US Dollar'
            className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:ring-blue-600'
          />
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>Full name in English</p>
        </div>

        {/* Currency Symbol */}
        <div>
          <label
            htmlFor='symbol'
            className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            Currency Symbol <span className='text-red-500'>*</span>
          </label>
          <input
            type='text'
            id='symbol'
            value={formData.symbol}
            onChange={e => handleChange('symbol', e.target.value)}
            required
            placeholder='$'
            className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:ring-blue-600'
          />
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>Symbol (e.g., $, ¥, €, £)</p>
        </div>

        {/* Decimal Configuration */}
        <div className='border-t pt-4 dark:border-gray-700'>
          <div className='mb-3 flex items-center gap-3'>
            <input
              type='checkbox'
              id='useDecimals'
              checked={formData.useDecimals}
              onChange={e => handleChange('useDecimals', e.target.checked)}
              className='rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700'
            />
            <label
              htmlFor='useDecimals'
              className='text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              Use Decimal Places
            </label>
          </div>

          {formData.useDecimals && (
            <div className='ml-6'>
              <label
                htmlFor='decimalPlaces'
                className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
              >
                Decimal Places
              </label>
              <select
                id='decimalPlaces'
                value={formData.decimalPlaces}
                onChange={e => handleChange('decimalPlaces', parseInt(e.target.value))}
                className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-600'
              >
                <option value={0}>0 (No decimals)</option>
                <option value={1}>1 digit</option>
                <option value={2}>2 digits</option>
                <option value={3}>3 digits</option>
                <option value={4}>4 digits</option>
              </select>
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                Most currencies use 2 decimal places. JPY and KRW typically use 0.
              </p>
            </div>
          )}
        </div>

        {/* Display Order */}
        <div>
          <label
            htmlFor='displayOrder'
            className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            Display Order
          </label>
          <input
            type='number'
            id='displayOrder'
            value={formData.displayOrder}
            onChange={e => handleChange('displayOrder', parseInt(e.target.value) || 0)}
            min='0'
            className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-600'
          />
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Lower numbers appear first (e.g., 1, 2, 3...)
          </p>
        </div>

        {/* Active Status */}
        <div className='flex items-center gap-3'>
          <input
            type='checkbox'
            id='isActive'
            checked={formData.isActive}
            onChange={e => handleChange('isActive', e.target.checked)}
            className='rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700'
          />
          <label
            htmlFor='isActive'
            className='text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            Active (available for use in plans)
          </label>
        </div>

        {/* Submit */}
        <div className='flex gap-4'>
          <button
            type='submit'
            disabled={loading}
            className='flex-1 rounded-md bg-blue-600 px-6 py-3 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 dark:bg-blue-700 dark:hover:bg-blue-800 dark:disabled:bg-gray-600'
          >
            {loading ? 'Updating...' : 'Update Currency'}
          </button>
          <button
            type='button'
            onClick={() => navigate('/currencies')}
            className='rounded-md bg-gray-200 px-6 py-3 text-gray-700 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditCurrencyPage;
